import { Head, useForm } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import InputError from "@/components/input-error"

interface PortfolioFormData {
  portfolio: {
    name: string
  }
}

interface PortfoliosNewProps {
  errors?: string[]
}

export default function PortfoliosNew({ errors = [] }: PortfoliosNewProps) {
  const { data, setData, post, processing } = useForm<PortfolioFormData>({
    portfolio: {
      name: "",
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/portfolios")
  }

  return (
    <AppLayout>
      <Head title="New Portfolio" />

      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Create New Portfolio
          </h1>
          <p className="text-muted-foreground">
            Start tracking your investments and dividend income
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Portfolio Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Portfolio Name *</Label>
                <Input
                  id="name"
                  type="text"
                  value={data.portfolio.name}
                  onChange={(e) =>
                    setData("portfolio", {
                      ...data.portfolio,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., Retirement Portfolio, Dividend Growth"
                  required
                />
                <InputError message={errors.find((e) => e.includes("name"))} />
              </div>

              {/* Error Messages */}
              {errors.length > 0 && (
                <div className="bg-destructive/15 rounded-md p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-destructive text-sm font-medium">
                        There were errors with your submission:
                      </h3>
                      <div className="text-destructive mt-2 text-sm">
                        <ul role="list" className="list-disc space-y-1 pl-5">
                          {errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button type="submit" disabled={processing}>
                  {processing ? "Creating..." : "Create Portfolio"}
                </Button>
                <Button type="button" variant="outline" asChild>
                  <a href="/portfolios">Cancel</a>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Tips for Portfolio Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-muted-foreground space-y-2 text-sm">
              <p>
                💡 <strong>Diversification:</strong> Consider creating separate
                portfolios for different goals (retirement, dividend income,
                growth).
              </p>
              <p>
                📊 <strong>Tracking:</strong> You can add individual stock
                positions after creating the portfolio.
              </p>
              <p>
                💰 <strong>Dividends:</strong> Track dividend payments to
                monitor your passive income growth.
              </p>
              <p>
                📈 <strong>Analytics:</strong> Use the analytics dashboard to
                monitor performance and risk metrics.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
